import { TgMailing } from "@/modules/telegram/interfaces"
import {
  BACKOFF_MS,
  COMMIT_MISMATCH_CYCLES,
  MAX_PAGE_ATTEMPTS,
  MAX_TOTAL,
} from "./constants"
import { buildPages } from "./chunkRecipients"
import { serializePage } from "./serializePage"
import { classifyError, isStuckInvariant, parseConflict } from "./classifyError"
import { getApiErrorMessage } from "@/core/utils"
import type {
  PagedEnqueueFailure,
  PagedEnqueuePause,
  PagedEnqueueResult,
  RunPagedEnqueueArgs,
  SerializedPage,
} from "./types"

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/** Внутренние сигналы управления потоком (не показываются оператору напрямую). */
class ControlSignal {
  constructor(public failure: PagedEnqueueFailure) {}
}
/** Страница получила 409 committed: досыл прекращается, идём к коммиту. */
class CommittedSignal {}

/**
 * 3-фазный протокол: init → страницы → commit (дельта §4).
 * Байты страницы сериализуются один раз и живут до конца коммита (нужны для
 * дозалива при 409 mismatch). expectedTotal = recipients.length, фиксирован до
 * init: дедупликация при загрузке не считается, сплиты меняют только
 * распределение получателей по страницам.
 */
export const runPagedEnqueue = async ({
  recipients,
  request,
  useMtProto,
  transport,
  callbacks,
  signal,
}: RunPagedEnqueueArgs): Promise<PagedEnqueueResult> => {
  const throwIfAborted = () => {
    if (signal?.aborted) throw new ControlSignal({ kind: "aborted-local" })
  }

  /** §3.4/§3.5: reject паузы → cancelled-by-user; onPause не передан → retry-exhausted.
   *  Без обёртки reject улетел бы из оркестратора сырой ошибкой (unhandled rejection). */
  const pauseOrThrow = async (pause: PagedEnqueuePause) => {
    if (!callbacks.onPause) {
      throw new ControlSignal({
        kind: "retry-exhausted",
        stage: pause.stage,
        pageIndex: pause.stage === "page" ? pause.pageIndex : undefined,
      })
    }
    try {
      await callbacks.onPause(pause)
    } catch {
      throw new ControlSignal({ kind: "cancelled-by-user" })
    }
  }

  if (recipients.length < 1 || recipients.length > MAX_TOTAL) {
    return {
      ok: false,
      failure: { kind: "invalid-recipient-count", count: recipients.length },
    }
  }

  try {
    throwIfAborted()
    const { id } = await transport.init({ ...request, useMtProto })
    callbacks.onEvent({ stage: "init", id })

    /** Фактически отправленные листья: pageIndex → байты (для дозалива). */
    const sentPages = new Map<number, SerializedPage>()
    let nextIndex = 0

    const failureFromHttp = (
      status: number,
      data: unknown,
      pageIndex: number,
      rawError: unknown,
    ): PagedEnqueueFailure | CommittedSignal => {
      if (status === 400) return { kind: "bad-request" }
      if (status === 404) return { kind: "not-found" }
      if (status === 409) {
        const conflict = parseConflict(data)
        if (!conflict) return { kind: "http", status }
        switch (conflict.conflict) {
          case "committed":
            return new CommittedSignal()
          case "closed":
            return { kind: "closed" }
          case "aborted":
            return { kind: "aborted" }
          case "limit":
            return {
              kind: "limit",
              reserved: conflict.reserved,
              maxTotal: conflict.maxTotal,
            }
          case "hash_mismatch":
            return { kind: "hash-mismatch", pageIndex }
        }
      }
      return {
        kind: "http",
        status,
        message: getApiErrorMessage(rawError),
      }
    }

    /**
     * Страница с политикой ретраев (дельта §5): network/5xx → backoff →
     * после исчерпания onPause (resolve = продолжить те же байты); 413 →
     * рекурсивный сплит на два последовательных листа; 4xx → failure.
     * Возвращает "committed", если гонка с коммитом выиграла у страницы.
     */
    const sendChunk = async (
      chunk: TgMailing.CallApiBody[],
      onAccepted: (pageIndex: number, enqueued: number) => void,
    ): Promise<"committed" | void> => {
      const pageIndex = nextIndex++
      const { bodyJson } = serializePage(pageIndex, chunk)
      let attempts = 0
      for (;;) {
        throwIfAborted()
        try {
          const { enqueued } = await transport.sendPage(id, pageIndex, bodyJson)
          sentPages.set(pageIndex, { pageIndex, bodyJson })
          onAccepted(pageIndex, enqueued)
          return
        } catch (e) {
          const classified = classifyError(e)
          // 413 (в т.ч. PARSING_ERROR от body-parser/ingress): реактивный сплит
          if (classified.type === "http" && classified.status === 413) {
            if (chunk.length === 1)
              throw new ControlSignal({ kind: "page-too-large" })
            const mid = Math.ceil(chunk.length / 2)
            await sendChunk(chunk.slice(0, mid), onAccepted)
            await sendChunk(chunk.slice(mid), onAccepted)
            return
          }
          // сетевая ошибка / 5xx / 503 → ретрай тех же байтов
          if (
            classified.type === "network" ||
            (classified.type === "http" && classified.status >= 500)
          ) {
            attempts += 1
            if (attempts >= MAX_PAGE_ATTEMPTS) {
              await pauseOrThrow({ stage: "page", pageIndex })
              attempts = 0
              continue
            }
            await sleep(BACKOFF_MS[attempts - 1])
            continue
          }
          // прочее — сворачиваем в failure
          const httpStatus = classified.type === "http" ? classified.status : 0
          const data = classified.type === "http" ? classified.data : undefined
          const failure = failureFromHttp(httpStatus, data, pageIndex, e)
          if (failure instanceof CommittedSignal) return "committed"
          throw new ControlSignal(failure)
        }
      }
    }

    for (const chunk of buildPages(recipients)) {
      throwIfAborted()
      const outcome = await sendChunk(chunk, (pageIndex, enqueued) => {
        callbacks.onEvent({ stage: "pages", pageIndex, enqueued })
      })
      if (outcome === "committed") break
    }

    // --- коммит с восстановлением после mismatch (дельта §4.5) ---
    throwIfAborted()
    callbacks.onEvent({ stage: "commit" })
    let commitAttempts = 0
    let cycles = 0
    for (;;) {
      try {
        const { state } = await transport.commit(id, recipients.length)
        return { ok: true, state }
      } catch (e) {
        const classified = classifyError(e)
        // stuck-инвариант 500 ВАЖНО проверить до network/5xx-ветки: не ретраить
        if (
          isStuckInvariant(
            classified.type === "http" ? classified.data : undefined,
          )
        ) {
          throw new ControlSignal({ kind: "commit-stuck" })
        }
        if (
          classified.type === "network" ||
          (classified.type === "http" && classified.status >= 500)
        ) {
          commitAttempts += 1
          if (commitAttempts >= MAX_PAGE_ATTEMPTS) {
            await pauseOrThrow({ stage: "commit" })
            commitAttempts = 0
            continue
          }
          await sleep(BACKOFF_MS[commitAttempts - 1])
          continue
        }
        if (classified.type === "http" && classified.status === 404) {
          throw new ControlSignal({ kind: "finalized-or-purged" })
        }
        if (classified.type === "http" && classified.status === 409) {
          const conflict = parseConflict(classified.data)
          if (conflict?.conflict === "closed")
            throw new ControlSignal({ kind: "closed" })
          if (conflict?.conflict !== "mismatch") {
            throw new ControlSignal({ kind: "http", status: 409 })
          }
          // --- сценарий дозалива (дельта §4.5) ---
          cycles += 1
          if (cycles > COMMIT_MISMATCH_CYCLES) {
            throw new ControlSignal({ kind: "commit-stuck" })
          }
          let prog: TgMailing.EnqueueProgressRes
          try {
            prog = await transport.progress(id)
          } catch (e) {
            const classified = classifyError(e)
            throw new ControlSignal({
              kind: "http",
              status: classified.type === "http" ? classified.status : 0,
              message: getApiErrorMessage(e),
            })
          }
          const done = new Set(prog.pagesDone ?? [])
          const missing = [...sentPages.keys()].filter((i) => !done.has(i))
          if (missing.length === 0 || prog.enqueued > recipients.length) {
            // досылать нечего / счётчики разошлись — короткое замыкание, не крутить циклы
            throw new ControlSignal({ kind: "commit-stuck" })
          }
          for (const pageIndex of missing) {
            throwIfAborted()
            const page = sentPages.get(pageIndex)
            if (!page) continue
            let resendAttempts = 0
            for (;;) {
              try {
                const { enqueued } = await transport.sendPage(
                  id,
                  pageIndex,
                  page.bodyJson,
                )
                sentPages.set(pageIndex, { pageIndex, bodyJson: page.bodyJson })
                callbacks.onEvent({ stage: "pages", pageIndex, enqueued })
                break
              } catch (retry) {
                const c2 = classifyError(retry)
                if (
                  c2.type === "network" ||
                  (c2.type === "http" && c2.status >= 500)
                ) {
                  resendAttempts += 1
                  if (resendAttempts >= MAX_PAGE_ATTEMPTS) {
                    await pauseOrThrow({ stage: "page", pageIndex })
                    resendAttempts = 0
                    continue
                  }
                  await sleep(BACKOFF_MS[resendAttempts - 1])
                  continue
                }
                // branch-ошибка при досыле — тот же маппинг, что у страниц
                // (иначе сырой RTK-объект вылетел бы из оркестратора rejection'ом)
                const failure = failureFromHttp(
                  c2.status,
                  c2.data,
                  pageIndex,
                  retry,
                )
                if (failure instanceof CommittedSignal) break
                throw new ControlSignal(failure)
              }
            }
          }
          continue // повторный commit
        }
        throw new ControlSignal({
          kind: "http",
          status: classified.type === "http" ? classified.status : 0,
          message: getApiErrorMessage(e),
        })
      }
    }
  } catch (e) {
    if (e instanceof ControlSignal) return { ok: false, failure: e.failure }
    throw e
  }
}
